// src/pages/Log.js
//
// Chunk 5 — the redesigned Log page. (v2 — adds By Category, By Person,
// extra filter chips, bill-match popup.)
//
// Replaces:  Transactions.js · Bills.js  (still in repo until chunk 9 cleanup)
//
// What it shows:
//   • Every transaction the user (and partner, if linked) has logged
//   • Including transactions created from "Mark paid" on a bill (those
//     carry bill_id and render with a "matched bill" hint)
//
// What it doesn't show:
//   • Bill *templates* — those live on Plan
//   • Upcoming/unpaid bills — those live on This Month "Coming up"
//
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useOutletContext, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useDataEvent, emitDataEvent, EVENTS } from '../lib/dataEvents'
import { usePartner } from '../lib/usePartner'
import { theme, atoms, firstName } from '../lib/theme'
import { useCurrency } from '../lib/CurrencyContext'
import QuickAddTransaction from '../components/QuickAddTransaction'
import BulkImport from '../components/BulkImport'
import { MatchPromptOverlay } from '../components/MatchPrompt'
import { detectMatch, unmatchTransactionFromBill, unmatchTransactionFromDebt } from '../lib/merchantMatching'
import { dedupeCategories, resolveCategoryId } from '../lib/categories'

const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const LONG_MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
]

const VIEW_MODES = [
  { id: 'flat',     label: 'Flat',        shortLabel: 'Flat' },
  { id: 'day',      label: 'By day',      shortLabel: 'Day' },
  { id: 'category', label: 'By category', shortLabel: 'Cat' },
  { id: 'person',   label: 'By person',   shortLabel: 'Who' },
]

const MORE_FILTERS = [
  { id: 'has_receipt', label: 'Has receipt' },
  { id: 'no_receipt',  label: 'No receipt' },
  { id: 'has_note',    label: 'Has note' },
]

const TAX_FILTER_OPTIONS = [
  { id: 'all',      label: 'All types' },
  { id: 'personal', label: 'Personal' },
  { id: 'business', label: 'Business' },
  { id: 'charity',  label: 'Charity' },
]

export default function Log() {
  const { formatCurrency, formatCurrencyPrecise } = useCurrency()
  const { partnerId, partnerProfile, loading: partnerLoading } = usePartner()
  const ctx = useOutletContext() || {}
  const openQuickAdd = ctx.openQuickAdd
  const isMobile = !!ctx.isMobile

  // Filter state
  const today = useMemo(() => new Date(), [])
  const [filterMonth, setFilterMonth] = useState(() => ({
    month: today.getMonth() + 1,
    year: today.getFullYear(),
  }))
  const [filterCategoryId, setFilterCategoryId] = useState('all')
  const [filterPerson, setFilterPerson] = useState('both')
  const [filterPaymentId, setFilterPaymentId] = useState('all')
  const [filterTax, setFilterTax] = useState('all')
  const [filterMoreSet, setFilterMoreSet] = useState(new Set())
  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useState('day')
  const [filterSheet, setFilterSheet] = useState(null)

  // Data state
  const [transactions, setTransactions] = useState([])
  const [allCategories, setAllCategories] = useState([])
  const [categoryBudgets, setCategoryBudgets] = useState([]) // for picking the current-month representative
  const [currentUserId, setCurrentUserId] = useState(null)
  const [paymentMethods, setPaymentMethods] = useState([])
  const [bills, setBills] = useState([])
  const [profile, setProfile] = useState(null)
  const [showBulkImport, setShowBulkImport] = useState(false)
  const [partnerName, setPartnerName] = useState(null)
  const [currentMonthPlan, setCurrentMonthPlan] = useState({})

  // Pagination (Chunk 10, triage #2) — replaces the old hard 500-row cap.
  const [hasMore, setHasMore] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [loadingMore, setLoadingMore] = useState(false)

  // UI state
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(new Set())
  const [editingTx, setEditingTx] = useState(null)
  const [repeatingTx, setRepeatingTx] = useState(null)
  const [matchPrompt, setMatchPrompt] = useState(null)       // Chunk 5: bill/debt confirm (edit-save + manual apply)
  const [debtMatchByTxId, setDebtMatchByTxId] = useState({}) // Chunk 5: txId → { paymentId, debtId, name, principal }
  const [openMenuId, setOpenMenuId] = useState(null)
  
  const [bulkSheet, setBulkSheet] = useState(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!partnerLoading) fetchAll() },
    [partnerId, partnerLoading, filterMonth?.month, filterMonth?.year])

  // Refetch when transactions change anywhere in the app.
  useDataEvent(
    [
      EVENTS.TRANSACTION_SAVED,
      EVENTS.TRANSACTION_UPDATED,
      EVENTS.TRANSACTION_DELETED,
      EVENTS.PAYMENT_METHOD_SAVED,
      EVENTS.PAYMENT_METHOD_DELETED,
      EVENTS.CATEGORY_SAVED,
      EVENTS.CATEGORY_UPDATED,
      EVENTS.CATEGORY_DELETED,
      EVENTS.BILL_SAVED,
      EVENTS.BILL_UPDATED,
      EVENTS.BILL_DELETED,
    ],
    () => fetchAll()
  )


  const PAGE_SIZE = 200

  // Base transaction query (select + month filter + stable ordering). Stable
  // sort = transaction_date DESC, id DESC (id is unique, so page boundaries
  // never drift). Shared by the initial load and "Load older".
  const buildTxQuery = (userIds) => {
    let q = supabase.from('transactions')
      .select(`
        id, amount, transaction_date, type,
        merchant_name, note, business_memo,
        category_id, budget_id, payment_method_id, bill_id, receipt_url,
        is_business, is_charitable, user_id,
        budget_categories(id, name, icon, parent_id),
        payment_methods(id, name, last_four),
        profiles!transactions_user_id_fkey(id, full_name),
        bills(id, name)
      `)
      .in('user_id', userIds)
      .order('transaction_date', { ascending: false })
      .order('id', { ascending: false })
    if (filterMonth) {
      const monthStart = `${filterMonth.year}-${String(filterMonth.month).padStart(2, '0')}-01`
      const endStr = filterMonth.month === 12
        ? `${filterMonth.year + 1}-01-01`
        : `${filterMonth.year}-${String(filterMonth.month + 1).padStart(2, '0')}-01`
      q = q.gte('transaction_date', monthStart).lt('transaction_date', endStr)
    }
    return q
  }

  // txId → debt-payment map for a set of loaded rows (Chunk 5 indicator).
  const fetchDebtMapFor = async (txIds) => {
    const debtMap = {}
    if (txIds.length) {
      const { data: dps } = await supabase.from('debt_payments')
        .select('id, transaction_id, debt_id, principal_amount, debts(name)')
        .in('transaction_id', txIds)
      ;(dps || []).forEach(dp => {
        if (dp.transaction_id) {
          debtMap[dp.transaction_id] = {
            paymentId: dp.id, debtId: dp.debt_id,
            name: dp.debts?.name || 'Debt', principal: dp.principal_amount,
          }
        }
      })
    }
    return debtMap
  }

  // Load the next page of older transactions and append (no drift — the list is
  // fully reloaded on any transaction data event, so offsets stay valid mid-session).
  const loadOlder = async () => {
    if (loadingMore || !hasMore) return
    setLoadingMore(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoadingMore(false); return }
    const userIds = [user.id, ...(partnerId ? [partnerId] : [])]
    const from = transactions.length
    const { data } = await buildTxQuery(userIds).range(from, from + PAGE_SIZE) // PAGE_SIZE+1 rows
    const rows = data || []
    const more = rows.length > PAGE_SIZE
    const page = more ? rows.slice(0, PAGE_SIZE) : rows
    setTransactions(prev => [...prev, ...page])
    setHasMore(more)
    const newMap = await fetchDebtMapFor(page.map(t => t.id))
    setDebtMatchByTxId(prev => ({ ...prev, ...newMap }))
    setLoadingMore(false)
  }

  const fetchAll = async () => {
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }
    const userIds = [user.id, ...(partnerId ? [partnerId] : [])]

    // Page 0: fetch PAGE_SIZE+1 so a returned surplus row signals "more exist".
    const txQuery = buildTxQuery(userIds).range(0, PAGE_SIZE)

    // Exact total for the "of M total" indicator — one head/count query per
    // reset (not per load-more).
    let countQuery = supabase.from('transactions').select('id', { count: 'exact', head: true }).in('user_id', userIds)
    if (filterMonth) {
      const monthStart = `${filterMonth.year}-${String(filterMonth.month).padStart(2, '0')}-01`
      const endStr = filterMonth.month === 12
        ? `${filterMonth.year + 1}-01-01`
        : `${filterMonth.year}-${String(filterMonth.month + 1).padStart(2, '0')}-01`
      countQuery = countQuery.gte('transaction_date', monthStart).lt('transaction_date', endStr)
    }

    const partnerProfilePromise = partnerId
      ? supabase.from('profiles').select('full_name').eq('id', partnerId).single()
      : Promise.resolve({ data: null })

    // Parallel: include the current-month budget query if applicable
    const isCurrentMonth = filterMonth &&
      filterMonth.month === today.getMonth() + 1 &&
      filterMonth.year === today.getFullYear()

    const currentBudgetPromise = isCurrentMonth
      ? supabase.from('budgets').select('id')
          .in('user_id', userIds).eq('month', filterMonth.month).eq('year', filterMonth.year)
          .eq('is_active', true).order('created_at').limit(1).maybeSingle()
      : Promise.resolve({ data: null })

    const [txRes, catsRes, pmRes, billsRes, profRes, partRes, budRes, countRes, catBudgetsRes] = await Promise.all([
      txQuery,
      supabase.from('budget_categories').select('id, name, icon, parent_id, budget_id, user_id, sort_order').in('user_id', userIds),
      supabase.from('payment_methods').select('*').in('user_id', userIds),
      supabase.from('bills').select('*').in('user_id', userIds).eq('is_active', true),
      supabase.from('profiles').select('*').eq('id', user.id).single(),
      partnerProfilePromise,
      currentBudgetPromise,
      countQuery,
      supabase.from('budgets').select('id, month, year').in('user_id', userIds),
    ])

    const rows = txRes.data || []
    const more = rows.length > PAGE_SIZE
    const page = more ? rows.slice(0, PAGE_SIZE) : rows
    setTransactions(page)
    setHasMore(more)
    setTotalCount(countRes.count != null ? countRes.count : page.length)
    setAllCategories(catsRes.data || [])
    setCategoryBudgets(catBudgetsRes.data || [])
    setCurrentUserId(user.id)
    setPaymentMethods(pmRes.data || [])
    setBills(billsRes.data || [])
    setProfile(profRes.data || null)
    setPartnerName(partRes.data?.full_name || null)

    // vs-plan: now that we have the budget, fetch its category limits (one extra roundtrip
    // but only when on current month)
    if (budRes.data) {
      const { data: planCats } = await supabase.from('budget_categories')
        .select('id, limit_amount').eq('budget_id', budRes.data.id)
      const plan = {}
      ;(planCats || []).forEach(c => { plan[c.id] = parseFloat(c.limit_amount || 0) })
      setCurrentMonthPlan(plan)
    } else {
      setCurrentMonthPlan({})
    }

    // Chunk 5: which loaded transactions are debt payments? Build txId → debt map
    // so the row can show a "→ [debt name]" indicator (bill matches come from the
    // transactions.bill_id join above; debts have no such column).
    setDebtMatchByTxId(await fetchDebtMapFor(page.map(t => t.id)))

    setLoading(false)
  }

  // Close menu on outside click
  useEffect(() => {
    if (!openMenuId) return
    const handler = (e) => { if (!e.target.closest('[data-row-menu]')) setOpenMenuId(null) }
    const esc = (e) => { if (e.key === 'Escape') setOpenMenuId(null) }
    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('keydown', esc)
    }
  }, [openMenuId])

  // Filtering
  const filteredTxs = useMemo(() => {
    return transactions.filter(tx => {
      if (search.trim()) {
        const q = search.toLowerCase().trim()
        const haystack = [
          tx.merchant_name, tx.note, tx.business_memo,
          tx.budget_categories?.name, tx.payment_methods?.name, tx.bills?.name,
        ].filter(Boolean).join(' ').toLowerCase()
        if (!haystack.includes(q)) return false
      }
      if (filterCategoryId !== 'all') {
        // filterCategoryId stores a category NAME (lowercased), not a UUID,
        // because each month creates fresh category rows. Match by name so
        // "Accessories" picks up every month's version.
        const txCat = allCategories.find(c => c.id === tx.category_id)
        const txCatName = (txCat?.name || '').trim().toLowerCase()
        if (txCatName !== filterCategoryId) return false
      }
      if (filterPerson === 'me' && profile && tx.user_id !== profile.id) return false
      if (filterPerson === 'partner' && (!partnerId || tx.user_id !== partnerId)) return false
      if (filterPaymentId !== 'all') {
        if (filterPaymentId === 'none' && tx.payment_method_id) return false
        if (filterPaymentId !== 'none' && tx.payment_method_id !== filterPaymentId) return false
      }
      if (filterTax === 'personal' && (tx.is_business || tx.is_charitable)) return false
      if (filterTax === 'business' && !tx.is_business) return false
      if (filterTax === 'charity' && !tx.is_charitable) return false
      if (filterMoreSet.has('has_receipt') && !tx.receipt_url) return false
      if (filterMoreSet.has('no_receipt') && tx.receipt_url) return false
      if (filterMoreSet.has('has_note') && !tx.note) return false
      return true
    })
  }, [transactions, search, filterCategoryId, filterPerson, filterPaymentId, filterTax, filterMoreSet, profile, partnerId])

  const totalAmount = filteredTxs.reduce((s, tx) => s + parseFloat(tx.amount || 0), 0)
  const txCount = filteredTxs.length
  const avgAmount = txCount > 0 ? totalAmount / txCount : 0

  const toggleSelect = useCallback((id) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])
  const clearSelection = () => setSelected(new Set())

  // Filter chips
  const activeFilterChips = useMemo(() => {
    const chips = []
    if (filterMonth) {
      chips.push({
        id: 'month',
        label: `${LONG_MONTHS[filterMonth.month - 1]} ${filterMonth.year}`,
        active: true, clearable: true,
        onClear: () => setFilterMonth(null),
        onClick: () => setFilterSheet('month'),
      })
    } else {
      chips.push({
        id: 'month', label: 'All time',
        active: false, onClick: () => setFilterSheet('month'),
      })
    }
    chips.push({
      id: 'category',
      label: filterCategoryId === 'all'
        ? 'All categories'
        : (() => {
            // filterCategoryId is now a lowercased name, not a UUID.
            const match = allCategories.find(c =>
              (c.name || '').trim().toLowerCase() === filterCategoryId
            )
            return match?.name || 'Category'
          })(),
      hasDown: true,
      active: filterCategoryId !== 'all',
      onClick: () => setFilterSheet('category'),
    })
    if (partnerId) {
      const meName = firstName(profile?.full_name) || 'You'
      const themName = firstName(partnerName) || 'Partner'
      chips.push({
        id: 'person',
        label: filterPerson === 'both' ? 'Both' : filterPerson === 'me' ? meName : themName,
        hasDown: true,
        active: filterPerson !== 'both',
        onClick: () => setFilterSheet('person'),
      })
    }
    chips.push({
      id: 'card',
      label: filterPaymentId === 'all'
        ? 'Any card'
        : filterPaymentId === 'none'
          ? 'No card'
          : (() => {
              const pm = paymentMethods.find(p => p.id === filterPaymentId)
              return pm ? `${pm.name}${pm.last_four ? ` · ${pm.last_four}` : ''}` : 'Card'
            })(),
      hasDown: true,
      active: filterPaymentId !== 'all',
      onClick: () => setFilterSheet('card'),
    })
    chips.push({
      id: 'tax',
      label: TAX_FILTER_OPTIONS.find(o => o.id === filterTax)?.label || 'All types',
      hasDown: true,
      active: filterTax !== 'all',
      onClick: () => setFilterSheet('tax'),
    })
    chips.push({
      id: 'more',
      label: filterMoreSet.size > 0
        ? `+${filterMoreSet.size} filter${filterMoreSet.size === 1 ? '' : 's'}`
        : 'More',
      active: filterMoreSet.size > 0,
      onClick: () => setFilterSheet('more'),
    })
    return chips
  }, [filterMonth, filterCategoryId, filterPerson, filterPaymentId, filterTax, filterMoreSet,
      allCategories, paymentMethods, partnerId, profile, partnerName])

  // CSV export
  const handleExportCSV = (onlySelected = false) => {
    const rows = onlySelected
      ? filteredTxs.filter(t => selected.has(t.id))
      : filteredTxs
    if (rows.length === 0) {
      window.alert('Nothing to export.')
      return
    }
    const header = ['Date','Merchant','Category','Amount','Payment','Tax','Memo','Note','Added by','Receipt URL']
    const escape = (v) => {
      const s = v == null ? '' : String(v)
      return `"${s.replace(/"/g, '""')}"`
    }
    const csvRows = rows.map(tx => [
      tx.transaction_date,
      tx.merchant_name || '',
      tx.budget_categories?.name || '',
      parseFloat(tx.amount || 0).toFixed(2),
      tx.payment_methods
        ? `${tx.payment_methods.name}${tx.payment_methods.last_four ? ` ····${tx.payment_methods.last_four}` : ''}`
        : '',
      tx.is_business ? 'Business' : tx.is_charitable ? 'Charity' : 'Personal',
      tx.business_memo || '', tx.note || '',
      tx.profiles?.full_name || '',
      tx.receipt_url || '',
    ].map(escape).join(','))
    const csv = [header.map(escape).join(','), ...csvRows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filterMonth
      ? `merq-vault-log-${filterMonth.year}-${String(filterMonth.month).padStart(2, '0')}.csv`
      : `merq-vault-log.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleBulkDelete = async () => {
    if (selected.size === 0) return
    if (!window.confirm(`Delete ${selected.size} transaction${selected.size === 1 ? '' : 's'}? This can't be undone.`)) return
    await supabase.from('transactions').delete().in('id', Array.from(selected))
    emitDataEvent(EVENTS.TRANSACTION_DELETED)
    clearSelection()
    fetchAll()
  }
  const handleBulkMarkBusiness = async () => {
    if (selected.size === 0) return
    await supabase.from('transactions')
      .update({ is_business: true, is_charitable: false })
      .in('id', Array.from(selected))
    clearSelection()
    fetchAll()
  }
  const handleBulkRecategorize = async (newCategoryId) => {
    if (selected.size === 0 || !newCategoryId) return
    await supabase.from('transactions')
      .update({ category_id: newCategoryId })
      .in('id', Array.from(selected))
    clearSelection()
    setBulkSheet(null)
    fetchAll()
  }

  const handleRowDelete = async (tx) => {
    if (!window.confirm(`Delete this transaction${tx.merchant_name ? ` from ${tx.merchant_name}` : ''}?`)) return
    await supabase.from('transactions').delete().eq('id', tx.id)
    setOpenMenuId(null)
    fetchAll()
  }

  // Bill match check (after edit save)
  // ─── Auto-match-then-confirm (Chunk 5) ───────────────────────
  // Exact normalized-name matching via the shared library, replacing the older
  // fuzzy in-Log matcher. A saved rule auto-applies silently; an unmatched hit
  // opens the shared MatchPrompt. Returns true if a prompt was opened.
  const loadBillsDebts = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { user: null, userIds: [], billsData: [], debtsData: [] }
    const userIds = [user.id, ...(partnerId ? [partnerId] : [])]
    const [{ data: billsData }, { data: debtsData }] = await Promise.all([
      supabase.from('bills').select('id, name, amount, category_id').in('user_id', userIds).eq('is_active', true),
      supabase.from('debts').select('id, name, current_balance, monthly_payment').in('user_id', userIds),
    ])
    return { user, userIds, billsData: billsData || [], debtsData: debtsData || [] }
  }

  const runMatchDetection = async (savedTx) => {
    if (!savedTx?.merchant_name || !savedTx?.amount) return false
    const { user, billsData, debtsData } = await loadBillsDebts()
    if (!user) return false
    const detection = await detectMatch({
      transaction: {
        id: savedTx.id, merchant_name: savedTx.merchant_name,
        amount: parseFloat(savedTx.amount), transaction_date: savedTx.transaction_date,
      },
      bills: billsData, debts: debtsData, userId: user.id,
    })
    if (detection.status === 'prompt') {
      setMatchPrompt({
        transactions: [{ id: savedTx.id, amount: parseFloat(savedTx.amount), transaction_date: savedTx.transaction_date }],
        kind: detection.kind, target: detection.target,
        bills: billsData, debts: debtsData, userId: user.id,
        merchantName: savedTx.merchant_name,
      })
      return true
    }
    return false
  }

  // Manual Override Rule — apply any transaction to a bill/debt via a picker.
  const openManualApply = async (tx) => {
    setOpenMenuId(null)
    const { user, billsData, debtsData } = await loadBillsDebts()
    if (!user) return
    setMatchPrompt({
      transactions: [{ id: tx.id, amount: parseFloat(tx.amount), transaction_date: tx.transaction_date }],
      kind: null, target: null,
      bills: billsData, debts: debtsData, userId: user.id,
      merchantName: tx.merchant_name,
    })
  }

  const handleUnmatchBill = async (tx) => {
    await unmatchTransactionFromBill(tx.id)
    fetchAll()
  }
  const handleUnmatchDebt = async (tx) => {
    await unmatchTransactionFromDebt(tx.id)
    fetchAll()
  }

  if (loading) {
    return (
      <div style={styles.loading}>
        <span>Loading log…</span>
      </div>
    )
  }

  const sharingFirst = partnerProfile ? firstName(partnerProfile.full_name) : null
  const hasFilters = !!(search || filterCategoryId !== 'all' || filterPerson !== 'both'
    || filterPaymentId !== 'all' || filterTax !== 'all' || filterMoreSet.size > 0)

  const rowProps = {
    selected, onToggleSelect: toggleSelect,
    onEdit: setEditingTx,
    onRepeat: setRepeatingTx,
    onDelete: handleRowDelete,
    openMenuId, setOpenMenuId,
    partnerId, profile, isMobile,
    debtMatchByTxId,
    onUnmatchBill: handleUnmatchBill,
    onUnmatchDebt: handleUnmatchDebt,
    onManualApply: openManualApply,
  }

  return (
    <div style={isMobile ? styles.pageMobile : styles.page}>

      {/* Header */}
      <div style={isMobile ? styles.headerMobile : styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.pageTitle}>Log</h1>
          <div style={atoms.pageMeta}>
            <span>Every transaction · sortable, filterable, printable</span>
            {sharingFirst && (
              <>
                <span style={atoms.pageMetaDot} />
                <span style={atoms.sharingPill}>
                  Sharing with {sharingFirst}
                </span>
              </>
            )}
          </div>
        </div>
        {!isMobile && (
          <div style={styles.headerActions}>
            {openQuickAdd && (
              <button
                type="button" className="btn-compact"
                style={{ ...styles.headerBtn, ...styles.headerBtnPlum }}
                onClick={openQuickAdd}
              >
                Add transaction
              </button>
            )}
            <button
              type="button" className="btn-compact"
              style={styles.headerBtn}
              onClick={() => setShowBulkImport(true)}
            >
              Import CSV
            </button>
            <button
              type="button" className="btn-compact"
              style={styles.headerBtn}
              onClick={() => handleExportCSV(false)}
            >
              Export CSV
            </button>
            <Link
              to="/report?month=current&print=auto" className="btn-compact"
              style={{ ...styles.headerBtn, ...styles.headerBtnGreen }}
            >
              Print month review
            </Link>
          </div>
        )}
      </div>

      {/* Filter bar */}
      <div style={isMobile ? styles.filterBarMobile : styles.filterBar}>
        <div style={styles.searchInput}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isMobile ? 'Search...' : 'Search merchant, category, note...'}
            style={styles.searchInputField}
            aria-label="Search transactions"
          />
          {search && (
            <button
              type="button" className="btn-compact"
              style={styles.searchClear}
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
        {!isMobile && <div style={styles.filterDivider} />}
        <div style={isMobile ? styles.chipScrollMobile : styles.chipScroll}>
          {activeFilterChips.map(chip => (
            <button
              key={chip.id}
              type="button" className="btn-compact"
              style={{
                ...styles.filterChip,
                ...(chip.active ? styles.filterChipActive : {}),
              }}
              onClick={chip.onClick}
            >
              {chip.label}
              {chip.clearable && (
                <span
                  style={{ ...styles.filterChipClear, fontSize: '14px', lineHeight: 1 }}
                  onClick={(e) => { e.stopPropagation(); chip.onClear() }}
                  role="button"
                  aria-label="Clear"
                >
                  ×
                </span>
              )}
              {chip.hasDown && !chip.clearable && (
                <span style={{ opacity: 0.6, fontSize: '11px', lineHeight: 1 }} aria-hidden="true">▾</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Sub-header */}
      <div style={isMobile ? styles.subHeaderMobile : styles.subHeader}>
        <div style={styles.totalDisplay}>
          <span style={styles.totalAmount}>{formatCurrency(totalAmount)}</span>
          <span style={styles.totalDetail}>
            <strong style={{ color: theme.colors.plum, fontWeight: 600 }}>{txCount}</strong>{' '}
            transaction{txCount === 1 ? '' : 's'}
            {filterMonth && <> in {LONG_MONTHS[filterMonth.month - 1]}</>}
            {txCount > 1 && (
              <> · avg <strong style={{ color: theme.colors.plum, fontWeight: 600 }}>{formatCurrency(avgAmount)}</strong></>
            )}
          </span>
        </div>
        <div style={isMobile ? styles.viewModesMobile : styles.viewModes}>
          {VIEW_MODES.filter(m => m.id !== 'person' || partnerId).map(mode => (
            <button
              key={mode.id}
              type="button" className="btn-compact"
              style={{
                ...styles.viewMode,
                ...(viewMode === mode.id ? styles.viewModeActive : {}),
              }}
              onClick={() => setViewMode(mode.id)}
            >
              {isMobile ? mode.shortLabel : mode.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      {filteredTxs.length === 0 ? (
        <EmptyState
          hasAnyTxs={transactions.length > 0}
          hasFilters={hasFilters}
          onAddTransaction={openQuickAdd}
          onClearFilters={() => {
            setSearch('')
            setFilterCategoryId('all')
            setFilterPerson('both')
            setFilterPaymentId('all')
            setFilterTax('all')
            setFilterMoreSet(new Set())
          }}
        />
      ) : (
        <div style={isMobile ? styles.bodyMobile : styles.body}>
          {viewMode === 'flat' && <FlatView txs={filteredTxs} {...rowProps} />}
          {viewMode === 'day' && <DayView txs={filteredTxs} {...rowProps} />}
          {viewMode === 'category' && (
            <CategoryView txs={filteredTxs} currentMonthPlan={currentMonthPlan} {...rowProps} />
          )}
          {viewMode === 'person' && partnerId && (
            <PersonView
              txs={filteredTxs}
              profile={profile} partnerName={partnerName}
              {...rowProps}
            />
          )}
        </div>
      )}

      {/* Pagination footer (Chunk 10). Shown even when a client-side filter hides
          every loaded row, so the user knows older rows exist to load. */}
      {transactions.length > 0 && (
        <div style={styles.pageFooter}>
          <div style={styles.pageCount}>
            {hasMore
              ? `Showing ${transactions.length} of ${totalCount} transactions`
              : `Showing ${transactions.length} transaction${transactions.length === 1 ? '' : 's'}`}
          </div>
          {hasMore && (
            <button
              type="button" className="btn-compact"
              style={styles.loadOlderBtn}
              onClick={loadOlder}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading…' : 'Load older →'}
            </button>
          )}
        </div>
      )}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div style={styles.bulkBar}>
          <div style={styles.bulkLeft}>
            <span style={styles.bulkCount}>
              {selected.size} transaction{selected.size === 1 ? '' : 's'} selected
            </span>
            <button
              type="button" className="btn-compact"
              style={styles.bulkClearBtn}
              onClick={clearSelection}
            >
              Clear
            </button>
          </div>
          <div style={styles.bulkActions}>
            <button
              type="button" className="btn-compact"
              style={styles.bulkBtn}
              onClick={() => setBulkSheet('recategorize')}
              title="Recategorize"
            >
              Recategorize
            </button>
            <button
              type="button" className="btn-compact"
              style={styles.bulkBtn}
              onClick={handleBulkMarkBusiness}
              title="Mark as business"
            >
              Mark business
            </button>
            <button
              type="button" className="btn-compact"
              style={styles.bulkBtn}
              onClick={() => handleExportCSV(true)}
              title="Export selected"
            >
              Export
            </button>
            <button
              type="button" className="btn-compact"
              style={{ ...styles.bulkBtn, ...styles.bulkBtnDanger }}
              onClick={handleBulkDelete}
              title="Delete"
            >
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Recategorize sheet */}
      {bulkSheet === 'recategorize' && (
        <RecategorizeSheet
          allCategories={allCategories}
          onClose={() => setBulkSheet(null)}
          onPick={handleBulkRecategorize}
          count={selected.size}
        />
      )}

      {/* Filter sheets */}
      {filterSheet && (
        <FilterSheet
          kind={filterSheet}
          onClose={() => setFilterSheet(null)}
          allCategories={allCategories}
          paymentMethods={paymentMethods}
          partnerName={partnerName}
          profileName={profile?.full_name}
          today={today}
          filterMonth={filterMonth} setFilterMonth={setFilterMonth}
          filterCategoryId={filterCategoryId} setFilterCategoryId={setFilterCategoryId}
          filterPerson={filterPerson} setFilterPerson={setFilterPerson}
          filterPaymentId={filterPaymentId} setFilterPaymentId={setFilterPaymentId}
          filterTax={filterTax} setFilterTax={setFilterTax}
          filterMoreSet={filterMoreSet} setFilterMoreSet={setFilterMoreSet}
        />
      )}

      {/* Edit sheet */}
      {editingTx && (
        <EditTransactionSheet
          tx={editingTx}
          allCategories={allCategories}
          categoryBudgets={categoryBudgets}
          currentUserId={currentUserId}
          paymentMethods={paymentMethods}
          canManualApply={!editingTx.bill_id && !debtMatchByTxId[editingTx.id]}
          onManualApply={(t) => { setEditingTx(null); openManualApply(t) }}
          onClose={() => setEditingTx(null)}
          onSaved={async (saved) => {
            // Skip re-detection if this tx was already matched (avoid re-prompting
            // an established bill/debt link when the user just edits a field).
            const wasMatched = !!(editingTx?.bill_id) || !!debtMatchByTxId[editingTx?.id]
            setEditingTx(null)
            await fetchAll()
            if (!wasMatched) await runMatchDetection(saved)
          }}
        />
      )}

      {/* Repeat */}
      {repeatingTx && (
        <QuickAddTransaction
          prefill={{
            amount: repeatingTx.amount,
            merchant_name: repeatingTx.merchant_name,
            category_id: repeatingTx.category_id,
          }}
          onClose={() => setRepeatingTx(null)}
          onSaved={() => { setRepeatingTx(null); fetchAll() }}
        />
      )}
      {/* Bulk import */}
      {showBulkImport && (
        <BulkImport
          partnerId={partnerId}
          onClose={() => setShowBulkImport(false)}
        />
      )}
      {/* Bill/debt match confirmation (Chunk 5) */}
      {matchPrompt && (
        <MatchPromptOverlay
          merchantName={matchPrompt.merchantName}
          transactions={matchPrompt.transactions}
          kind={matchPrompt.kind}
          target={matchPrompt.target}
          bills={matchPrompt.bills}
          debts={matchPrompt.debts}
          userId={matchPrompt.userId}
          onDone={() => { setMatchPrompt(null); fetchAll() }}
          onCancel={() => { setMatchPrompt(null); fetchAll() }}
        />
      )}
    </div>
  )
}

// ─── Views ───────────────────────────────────────────────────

function FlatView({ txs, ...rowProps }) {
  return (
    <div style={styles.rowGroup}>
      {txs.map(tx => (
        <TxnRow key={tx.id} tx={tx} {...rowProps} showDate />
      ))}
    </div>
  )
}

function DayView({ txs, ...rowProps }) {
  const { formatCurrency, formatCurrencyPrecise } = useCurrency()
  const groups = useMemo(() => {
    const m = new Map()
    txs.forEach(tx => {
      const d = tx.transaction_date
      if (!m.has(d)) m.set(d, [])
      m.get(d).push(tx)
    })
    return Array.from(m.entries()).sort((a, b) => b[0].localeCompare(a[0]))
  }, [txs])

  const today = new Date()
  const todayStr = today.toISOString().split('T')[0]
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1)
  const yesterdayStr = yesterday.toISOString().split('T')[0]

  return (
    <>
      {groups.map(([date, dayTxs]) => {
        const total = dayTxs.reduce((s, tx) => s + parseFloat(tx.amount || 0), 0)
        const dt = new Date(date + 'T12:00:00')
        const label = date === todayStr ? 'Today'
          : date === yesterdayStr ? 'Yesterday'
          : dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
        return (
          <div key={date} style={styles.group}>
            <div style={styles.groupHead}>
              <div style={styles.groupTitle}>
                <span style={styles.groupName}>{label}</span>
                <span style={styles.groupCount}>
                  {dayTxs.length} transaction{dayTxs.length === 1 ? '' : 's'}
                </span>
              </div>
              <div style={styles.groupTotal}>
                <span style={styles.groupTotalAmount}>{formatCurrency(total)}</span>
              </div>
            </div>
            <div style={styles.rowGroup}>
              {dayTxs.map(tx => (
                <TxnRow key={tx.id} tx={tx} {...rowProps} showDate={false} />
              ))}
            </div>
          </div>
        )
      })}
    </>
  )
}

function CategoryView({ txs, currentMonthPlan, ...rowProps }) {
  const { formatCurrency, formatCurrencyPrecise } = useCurrency()
  const groups = useMemo(() => {
    const m = new Map()
    txs.forEach(tx => {
      const cat = tx.budget_categories
      const key = cat ? cat.id : 'uncategorized'
      const name = cat ? cat.name : 'Uncategorized'
      if (!m.has(key)) m.set(key, { id: key, name, icon: cat?.icon, txs: [] })
      m.get(key).txs.push(tx)
    })
    return Array.from(m.values())
      .map(g => ({ ...g, total: g.txs.reduce((s, t) => s + parseFloat(t.amount || 0), 0) }))
      .sort((a, b) => b.total - a.total)
  }, [txs])

  return (
    <>
      {groups.map(g => {
        const planLimit = currentMonthPlan[g.id] || 0
        const vsPlan = planLimit > 0 ? planLimit - g.total : null
        const vsPlanColor = vsPlan === null ? null
          : vsPlan >= 0 ? theme.colors.green : theme.colors.orange
        return (
          <div key={g.id} style={styles.group}>
            <div style={styles.groupHead}>
              <div style={styles.groupTitle}>
                <span style={styles.groupName}>{g.name}</span>
                <span style={styles.groupCount}>
                  {g.txs.length} transaction{g.txs.length === 1 ? '' : 's'}
                </span>
              </div>
              <div style={styles.groupTotal}>
                <span style={styles.groupTotalAmount}>{formatCurrency(g.total)}</span>
                {vsPlan !== null && (
                  <span style={{ ...styles.groupTotalVs, color: vsPlanColor }}>
                    {vsPlan >= 0
                      ? `${formatCurrency(vsPlan)} under plan`
                      : `${formatCurrency(Math.abs(vsPlan))} over plan`}
                  </span>
                )}
              </div>
            </div>
            <div style={styles.rowGroup}>
              {g.txs.map(tx => (
                <TxnRow key={tx.id} tx={tx} {...rowProps} showDate />
              ))}
            </div>
          </div>
        )
      })}
    </>
  )
}

function PersonView({ txs, profile, partnerName, partnerId, ...rowProps }) {
  const { formatCurrency, formatCurrencyPrecise } = useCurrency()
  const myTxs = txs.filter(t => t.user_id === profile?.id)
  const theirTxs = txs.filter(t => t.user_id === partnerId)
  const meName = firstName(profile?.full_name) || 'You'
  const themName = firstName(partnerName) || 'Partner'
  const groups = [
    { name: meName, txs: myTxs, total: myTxs.reduce((s, t) => s + parseFloat(t.amount || 0), 0) },
    { name: themName, txs: theirTxs, total: theirTxs.reduce((s, t) => s + parseFloat(t.amount || 0), 0) },
  ].filter(g => g.txs.length > 0)
  return (
    <>
      {groups.map(g => (
        <div key={g.name} style={styles.group}>
          <div style={styles.groupHead}>
            <div style={styles.groupTitle}>
              <span style={styles.groupName}>{g.name}</span>
              <span style={styles.groupCount}>
                {g.txs.length} transaction{g.txs.length === 1 ? '' : 's'}
              </span>
            </div>
            <div style={styles.groupTotal}>
              <span style={styles.groupTotalAmount}>{formatCurrency(g.total)}</span>
            </div>
          </div>
          <div style={styles.rowGroup}>
            {g.txs.map(tx => (
              <TxnRow
                key={tx.id} tx={tx}
                profile={profile} partnerId={partnerId}
                {...rowProps} showDate
              />
            ))}
          </div>
        </div>
      ))}
    </>
  )
}

// ─── Row ─────────────────────────────────────────────────────

function TxnRow({
  tx, selected, onToggleSelect,
  onEdit, onRepeat, onDelete,
  openMenuId, setOpenMenuId,
  partnerId, profile, isMobile, showDate,
  debtMatchByTxId, onUnmatchBill, onUnmatchDebt, onManualApply,
}) {
  const { formatCurrency, formatCurrencyPrecise } = useCurrency()
  const isSelected = selected ? selected.has(tx.id) : false
  const cat = tx.budget_categories
  const pm = tx.payment_methods
  const personName = tx.profiles?.full_name ? firstName(tx.profiles.full_name) : null
  const dt = new Date(tx.transaction_date + 'T12:00:00')
  const dateLabel = `${SHORT_MONTHS[dt.getMonth()]} ${dt.getDate()}`
  const taxColor = tx.is_business
    ? theme.colors.orange
    : tx.is_charitable ? theme.colors.rose : null
  const menuOpen = openMenuId === tx.id

  // Chunk 5: a tx is matched to a bill (transactions.bill_id) or a debt
  // (debt_payments row). Shown as a tappable "→ [name]" unmatch indicator.
  const billName = tx.bills?.name || null
  const debtMatch = debtMatchByTxId ? debtMatchByTxId[tx.id] : null

  const metaParts = []
  if (cat?.name && !isMobile) metaParts.push(cat.name)
  // tx.note now renders as the dedicated inline-editable memo line below (Chunk 10b).
  if (tx.is_business && tx.business_memo) metaParts.push(tx.business_memo)
  const meta = metaParts.join(' · ')

  if (isMobile) {
    return (
      <div
        style={{
          ...styles.txnRowMobile,
          ...(isSelected ? styles.txnRowSelected : {}),
        }}
        onClick={(e) => {
          if (e.target.closest('[data-no-row-click]')) return
          onEdit(tx)
        }}
        role="button"
      >
        {onToggleSelect && (
          <button
            type="button" className="btn-compact"
            data-no-row-click="true"
            style={{
              ...styles.txnCheck,
              ...(isSelected ? styles.txnCheckSelected : {}),
            }}
            onClick={(e) => { e.stopPropagation(); onToggleSelect(tx.id) }}
            aria-label={isSelected ? 'Deselect' : 'Select'}
          >
            {isSelected && <span style={{ color: '#fff', fontSize: '11px', lineHeight: 1 }} aria-hidden="true">✓</span>}
          </button>
        )}
        <div style={styles.txnRowMobileBody}>
          <div style={styles.txnRowMobileTop}>
            <div style={styles.txnMerchantMobile}>
              <span style={styles.txnMerchantName}>{tx.merchant_name || 'Transaction'}</span>
              <span style={styles.txnMerchantMeta}>
                {showDate ? `${dateLabel} · ` : ''}{cat?.name || 'Uncategorized'}
              </span>
              <MemoLine tx={tx} />
            </div>
            <div style={{ ...styles.txnAmount, ...(taxColor ? { color: taxColor } : {}) }}>
              {formatCurrencyPrecise(tx.amount)}
            </div>
          </div>
          <div style={styles.txnRowMobileFoot}>
            {billName && <MatchIndicator name={billName} onUnmatch={() => onUnmatchBill(tx)} />}
            {debtMatch && <MatchIndicator name={debtMatch.name} onUnmatch={() => onUnmatchDebt(tx)} />}
            {tx.receipt_url && (
              <a
                href={tx.receipt_url} target="_blank" rel="noreferrer"
                data-no-row-click="true"
                style={styles.txnReceiptLinkMobile}
                onClick={(e) => e.stopPropagation()}
              >
                Receipt
              </a>
            )}
            {tx.is_business && <span style={styles.taxPillBusiness}>Business</span>}
            {tx.is_charitable && <span style={styles.taxPillCharity}>Charity</span>}
            {pm && (
              <span style={styles.pmTagMobile}>
                {pm.name}{pm.last_four ? ` · ${pm.last_four}` : ''}
              </span>
            )}
            {partnerId && personName && (
              <span style={styles.whoTagMobile}>{personName}</span>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        ...styles.txnRow,
        ...(isSelected ? styles.txnRowSelected : {}),
      }}
      onClick={(e) => {
        if (e.target.closest('[data-no-row-click]')) return
        onEdit(tx)
      }}
      role="button"
    >
      {onToggleSelect && (
        <button
          type="button" className="btn-compact"
          data-no-row-click="true"
          style={{
            ...styles.txnCheck,
            ...(isSelected ? styles.txnCheckSelected : {}),
          }}
          onClick={(e) => { e.stopPropagation(); onToggleSelect(tx.id) }}
          aria-label={isSelected ? 'Deselect' : 'Select'}
        >
          {isSelected && <span style={{ color: '#fff', fontSize: '11px', lineHeight: 1 }} aria-hidden="true">✓</span>}
        </button>
      )}
      <div style={styles.txnDate}>
        {showDate && <span style={styles.txnDay}>{dateLabel}</span>}
      </div>
      <ReceiptIndicator url={tx.receipt_url} />
      <div style={styles.txnMerchant}>
        <span style={styles.txnMerchantName}>
          {tx.merchant_name || cat?.name || 'Transaction'}
        </span>
        {meta && <span style={styles.txnMerchantMeta}>{meta}</span>}
        <MemoLine tx={tx} />
        {(billName || debtMatch) && (
          <span style={styles.txnMatchRow}>
            {billName && <MatchIndicator name={billName} onUnmatch={() => onUnmatchBill(tx)} />}
            {debtMatch && <MatchIndicator name={debtMatch.name} onUnmatch={() => onUnmatchDebt(tx)} />}
          </span>
        )}
      </div>
      <div style={styles.txnCategory}>
        <CategorySwatch cat={cat} />
        <span style={styles.txnCategoryLabel}>{cat?.name || 'Uncategorized'}</span>
      </div>
      <div style={styles.txnPayment}>
        {partnerId && personName && (
          <span style={styles.txnWho}>{personName}</span>
        )}
        <span style={styles.txnPm}>
          {pm
            ? `${pm.name}${pm.last_four ? ` · ${pm.last_four}` : ''}`
            : '—'}
        </span>
      </div>
      <div style={{
        ...styles.txnAmount,
        ...(taxColor ? { color: taxColor } : {}),
      }}>
        {tx.is_business && <span style={styles.txnBusinessDot} aria-hidden="true">⊙ </span>}
        {formatCurrencyPrecise(tx.amount)}
      </div>
      <div data-no-row-click="true" data-row-menu style={{ position: 'relative' }}>
        <button
          type="button" className="btn-compact"
          style={styles.txnMenu}
          onClick={(e) => {
            e.stopPropagation()
            setOpenMenuId(menuOpen ? null : tx.id)
          }}
          aria-label="More actions"
        >
          …
        </button>
        {menuOpen && (
          <div style={styles.menuPopover} role="menu">
            <button
              type="button" className="btn-compact"
              style={styles.menuItem}
              onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onEdit(tx) }}
            >
              Edit
            </button>
            <button
              type="button" className="btn-compact"
              style={styles.menuItem}
              onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onRepeat(tx) }}
            >
              Repeat
            </button>
            {!billName && !debtMatch && onManualApply && (
              <button
                type="button" className="btn-compact"
                style={styles.menuItem}
                onClick={(e) => { e.stopPropagation(); onManualApply(tx) }}
              >
                Apply to bill or debt
              </button>
            )}
            <button
              type="button" className="btn-compact"
              style={{ ...styles.menuItem, color: theme.colors.rose }}
              onClick={(e) => { e.stopPropagation(); onDelete(tx) }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// Chunk 10b — inline-editable memo on each Log row (writes transactions.note).
// Shows the note as a muted line under the merchant name, or a subtle "+ memo"
// when empty; tap either to edit in place. Saves on blur/Enter, cancels on Esc.
function MemoLine({ tx }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(tx.note || '')
  const [saving, setSaving] = useState(false)
  const inputRef = useRef(null)
  const cancelling = useRef(false) // set on Escape so the resulting blur doesn't save

  useEffect(() => { setValue(tx.note || '') }, [tx.note])
  useEffect(() => { if (editing) setTimeout(() => inputRef.current?.focus(), 0) }, [editing])

  const save = async () => {
    if (cancelling.current) { cancelling.current = false; return }
    if (saving) return
    const next = value.trim().slice(0, 200)
    if (next === (tx.note || '')) { setEditing(false); return }
    setSaving(true)
    await supabase.from('transactions').update({ note: next || null }).eq('id', tx.id)
    emitDataEvent(EVENTS.TRANSACTION_SAVED)
    setSaving(false)
    setEditing(false)
  }
  const cancel = () => { cancelling.current = true; setValue(tx.note || ''); setEditing(false) }

  if (editing) {
    return (
      <input
        ref={inputRef}
        data-no-row-click="true"
        style={styles.memoInput}
        value={value}
        maxLength={200}
        placeholder="Add a memo…"
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); save() }
          else if (e.key === 'Escape') { e.preventDefault(); cancel() }
        }}
      />
    )
  }
  return (
    <span
      data-no-row-click="true"
      style={tx.note ? styles.memoText : styles.memoAdd}
      onClick={(e) => { e.stopPropagation(); setEditing(true) }}
      title={tx.note || 'Add a memo'}
    >
      {tx.note || '+ memo'}
    </span>
  )
}

function ReceiptIndicator({ url }) {
  if (url) {
    return (
      <a
        href={url} target="_blank" rel="noreferrer"
        data-no-row-click="true"
        style={styles.txnReceipt}
        onClick={(e) => e.stopPropagation()}
        title="View receipt"
        aria-label="View receipt"
      >
        ↗
      </a>
    )
  }
  return (
    <div style={{ ...styles.txnReceipt, opacity: 0.4 }} aria-label="No receipt">
      —
    </div>
  )
}

function CategorySwatch({ cat }) {
  if (!cat) return <span style={{ ...styles.swatch, background: theme.colors.inkQuiet }} />
  const lower = cat.name.toLowerCase()
  let color = theme.colors.plumSoft
  if (lower.includes('food') || lower.includes('grocer') || lower.includes('restaurant') || lower.includes('coffee'))
    color = theme.colors.green
  else if (lower.includes('transport') || lower.includes('gas') || lower.includes('uber') || lower.includes('car'))
    color = theme.colors.orange
  else if (lower.includes('shop') || lower.includes('clothing') || lower.includes('amazon'))
    color = theme.colors.purple
  else if (lower.includes('health') || lower.includes('medical') || lower.includes('pharm'))
    color = theme.colors.rose
  else if (lower.includes('saving') || lower.includes('goal'))
    color = theme.colors.purple
  else if (lower.includes('debt'))
    color = theme.colors.orange
  return <span style={{ ...styles.swatch, background: color }} />
}

// ─── Filter sheets ───────────────────────────────────────────

function FilterSheet({
  kind, onClose,
  allCategories, paymentMethods, partnerName, profileName, today,
  filterMonth, setFilterMonth,
  filterCategoryId, setFilterCategoryId,
  filterPerson, setFilterPerson,
  filterPaymentId, setFilterPaymentId,
  filterTax, setFilterTax,
  filterMoreSet, setFilterMoreSet,
}) {
  const titleMap = {
    month: 'Date range', category: 'Category', person: 'Person',
    card: 'Payment method', tax: 'Tax type', more: 'More filters',
  }
  return (
    <Sheet onClose={onClose} title={titleMap[kind]}>
      <div style={styles.sheetBody}>
        {kind === 'month' && (
          <MonthPicker
            today={today}
            filterMonth={filterMonth}
            onPick={(m) => { setFilterMonth(m); onClose() }}
          />
        )}
        {kind === 'category' && (
          <PickerList
            items={[
              { id: 'all', label: 'All categories' },
              ...dedupeCategoryNames(allCategories).map(c => ({
                id: (c.name || '').trim().toLowerCase(),
                label: c.name,
              })),
            ]}
            activeId={filterCategoryId}
            onPick={(id) => { setFilterCategoryId(id); onClose() }}
          />
        )}
        {kind === 'person' && (
          <PickerList
            items={[
              { id: 'both',    label: 'Both' },
              { id: 'me',      label: firstName(profileName) || 'You' },
              { id: 'partner', label: firstName(partnerName) || 'Partner' },
            ]}
            activeId={filterPerson}
            onPick={(id) => { setFilterPerson(id); onClose() }}
          />
        )}
        {kind === 'card' && (
          <PickerList
            items={[
              { id: 'all',  label: 'Any card' },
              { id: 'none', label: 'No card / cash' },
              ...paymentMethods.map(pm => ({
                id: pm.id,
                label: `${pm.name}${pm.last_four ? ` · ${pm.last_four}` : ''}`,
              })),
            ]}
            activeId={filterPaymentId}
            onPick={(id) => { setFilterPaymentId(id); onClose() }}
          />
        )}
        {kind === 'tax' && (
          <PickerList
            items={TAX_FILTER_OPTIONS}
            activeId={filterTax}
            onPick={(id) => { setFilterTax(id); onClose() }}
          />
        )}
        {kind === 'more' && (
          <MoreFilters
            filterMoreSet={filterMoreSet}
            setFilterMoreSet={setFilterMoreSet}
            onClose={onClose}
          />
        )}
      </div>
    </Sheet>
  )
}

function MonthPicker({ today, filterMonth, onPick }) {
  const opts = []
  for (let i = 2; i >= -12; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1)
    opts.push({ y: d.getFullYear(), m: d.getMonth() + 1 })
  }
  return (
    <div>
      <button
        type="button" className="btn-compact"
        style={{
          ...styles.pickerRow,
          background: !filterMonth ? theme.colors.greenSoft : 'transparent',
        }}
        onClick={() => onPick(null)}
      >
        <span style={styles.pickerLabel}>All time</span>
        {!filterMonth && <span style={{ color: theme.colors.green, fontSize: '14px', lineHeight: 1 }} aria-hidden="true">✓</span>}
      </button>
      {opts.map(o => {
        const active = filterMonth && filterMonth.month === o.m && filterMonth.year === o.y
        const isThis = today.getMonth() + 1 === o.m && today.getFullYear() === o.y
        return (
          <button
            key={`${o.y}-${o.m}`}
            type="button" className="btn-compact"
            style={{
              ...styles.pickerRow,
              background: active ? theme.colors.greenSoft : 'transparent',
            }}
            onClick={() => onPick({ month: o.m, year: o.y })}
          >
            <span style={styles.pickerLabel}>
              {LONG_MONTHS[o.m - 1]} {o.y}
              {isThis && <span style={styles.pickerCurrentBadge}>now</span>}
            </span>
            {active && <span style={{ color: theme.colors.green, fontSize: '14px', lineHeight: 1 }} aria-hidden="true">✓</span>}
          </button>
        )
      })}
    </div>
  )
}

function PickerList({ items, activeId, onPick }) {
  return (
    <div>
      {items.map(item => (
        <button
          key={item.id}
          type="button" className="btn-compact"
          style={{
            ...styles.pickerRow,
            background: activeId === item.id ? theme.colors.greenSoft : 'transparent',
          }}
          onClick={() => onPick(item.id)}
        >
          <span style={styles.pickerLabel}>{item.label}</span>
          {activeId === item.id && <span style={{ color: theme.colors.green, fontSize: '14px', lineHeight: 1 }} aria-hidden="true">✓</span>}
        </button>
      ))}
    </div>
  )
}

function MoreFilters({ filterMoreSet, setFilterMoreSet, onClose }) {
  const toggle = (id) => {
    setFilterMoreSet(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else {
        if (id === 'has_receipt') next.delete('no_receipt')
        if (id === 'no_receipt') next.delete('has_receipt')
        next.add(id)
      }
      return next
    })
  }
  return (
    <div>
      {MORE_FILTERS.map(f => (
        <button
          key={f.id}
          type="button" className="btn-compact"
          style={{
            ...styles.pickerRow,
            background: filterMoreSet.has(f.id) ? theme.colors.greenSoft : 'transparent',
          }}
          onClick={() => toggle(f.id)}
        >
          <span style={styles.pickerLabel}>{f.label}</span>
          {filterMoreSet.has(f.id) && <span style={{ color: theme.colors.green, fontSize: '14px', lineHeight: 1 }} aria-hidden="true">✓</span>}
        </button>
      ))}
      <div style={styles.moreFooter}>
        <button
          type="button" className="btn-compact"
          style={styles.sheetSecondary}
          onClick={() => { setFilterMoreSet(new Set()); onClose() }}
        >
          Clear filters
        </button>
        <button
          type="button"
          style={styles.sheetPrimary}
          onClick={onClose}
        >
          Done
        </button>
      </div>
    </div>
  )
}

function RecategorizeSheet({ allCategories, onClose, onPick, count }) {
  const cats = dedupeCategoryNames(allCategories)
  return (
    <Sheet onClose={onClose} title={`Recategorize ${count} transaction${count === 1 ? '' : 's'}`}>
      <div style={styles.sheetBody}>
        {cats.length === 0 ? (
          <div style={styles.sheetEmpty}>No categories yet. Set up your plan first.</div>
        ) : (
          cats.map(c => (
            <button
              key={c.id}
              type="button" className="btn-compact"
              style={styles.pickerRow}
              onClick={() => onPick(c.id)}
            >
              <span style={styles.pickerLabel}>
                {c.name}
              </span>
            </button>
          ))
        )}
      </div>
    </Sheet>
  )
}

// Flat name-only dedupe for the filter + Recategorize sheets. Hierarchical
// pickers must use dedupeCategories (src/lib/categories.js) instead.
function dedupeCategoryNames(cats) {
  // Dedupe by name only — each month creates fresh budget_categories rows,
  // so we get many "Accessories" entries with different parent_ids/IDs.
  // The filter matches by name (see filterCategoryId logic in main fetch),
  // so the picker only needs one entry per unique name.
  const seen = new Map()
  cats.forEach(c => {
    const key = (c.name || '').trim().toLowerCase()
    if (!key) return
    if (!seen.has(key)) seen.set(key, c)
  })
  return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name))
}

// ─── Edit sheet (Phone 2 field-list) ─────────────────────────

function EditTransactionSheet({ tx, allCategories, categoryBudgets, currentUserId, paymentMethods, onClose, onSaved, onManualApply, canManualApply }) {
  const { formatCurrency, formatCurrencyPrecise } = useCurrency()
  const [merchantName, setMerchantName] = useState(tx.merchant_name || '')
  // Show two decimals up front (e.g. "45.00", "9.73") so the field doesn't read
  // "$45" until the user clicks in (Chunk 10b).
  const [amount, setAmount] = useState(
    tx.amount != null && tx.amount !== '' ? parseFloat(tx.amount).toFixed(2) : ''
  )
  const [categoryId, setCategoryId] = useState(tx.category_id || '')
  const [date, setDate] = useState(tx.transaction_date)
  const dateInputRef = useRef(null)
  const [paymentMethodId, setPaymentMethodId] = useState(tx.payment_method_id || null)
  const [taxStatus, setTaxStatus] = useState(
    tx.is_business ? 'business' : tx.is_charitable ? 'charity' : 'personal'
  )
  const [businessMemo, setBusinessMemo] = useState(tx.business_memo || '')
  const [note, setNote] = useState(tx.note || '')
  const [view, setView] = useState('main')
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)

  // Deduped by canonical parent::child name (src/lib/categories.js). The old
  // name-only dedupe could keep a subcategory whose parent_id pointed at a
  // different month's parent row, hiding it from the grouped picker.
  const cats = useMemo(
    () => dedupeCategories(allCategories, { budgets: categoryBudgets, userId: currentUserId })
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [allCategories, categoryBudgets, currentUserId]
  )
  // The tx's category_id points to one month's category row, but the deduped
  // picker holds a different UUID for the same name (categories are recreated
  // per month). Resolve the picker's equivalent category BY CANONICAL NAME so
  // the current category shows selected/highlighted — without rewriting
  // category_id on save.
  const effectiveCatId = useMemo(
    () => resolveCategoryId(categoryId, allCategories, cats) || categoryId,
    [cats, categoryId, allCategories]
  )
  const selectedCategory = cats.find(c => c.id === effectiveCatId)
  const selectedPM = paymentMethods.find(p => p.id === paymentMethodId)
  const parentCats = cats.filter(c => !c.parent_id)
  const childCats = (parentId) => cats.filter(c => c.parent_id === parentId)

  // Always two decimals, no currency rounding (the $ sign is decoration outside
  // the input). Empty when blank. (Chunk 10b hotfix — was formatCurrency, which
  // rounded "$4.52" to "$5".)
  const formattedAmount = useMemo(() => {
    if (!amount) return ''
    const num = parseFloat(amount)
    if (isNaN(num)) return ''
    return num.toFixed(2)
  }, [amount])

  const formattedDate = useMemo(() => {
    if (!date) return '—'
    try {
      return new Date(date + 'T12:00:00').toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      })
    } catch { return date }
  }, [date])

  const handleSave = async () => {
    if (saving) return
    if (!merchantName || !amount || !categoryId) return
    setSaving(true)
    const isBusiness = taxStatus === 'business'
    const isCharitable = taxStatus === 'charity'
    const updates = {
      merchant_name: merchantName,
      amount: parseFloat(amount),
      category_id: categoryId,
      transaction_date: date,
      payment_method_id: paymentMethodId || null,
      is_business: isBusiness,
      is_charitable: isCharitable,
      business_memo: isBusiness ? (businessMemo || null) : null,
      note: note || null,
    }
    await supabase.from('transactions').update(updates).eq('id', tx.id)
    setSaving(false)
    onSaved({ id: tx.id, ...updates })
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') {
        if (view !== 'main') { setView('main'); return }
        if (editing) { setEditing(null); return }
        onClose()
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleSave()
    }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [view, editing, merchantName, amount, categoryId, date, paymentMethodId, taxStatus, businessMemo, note])

  if (view === 'category') {
    return (
      <Sheet onClose={onClose} title="Select category" onBack={() => setView('main')}>
        <div style={styles.sheetBody}>
          {parentCats.length === 0 ? (
            <div style={styles.sheetEmpty}>No categories yet.</div>
          ) : (
            parentCats.map(parent => {
              const subs = childCats(parent.id)
              return (
                <div key={parent.id}>
                  <button
                    type="button" className="btn-compact"
                    style={{
                      ...styles.pickerRow,
                      background: effectiveCatId === parent.id ? theme.colors.greenSoft : 'transparent',
                      fontWeight: 600,
                    }}
                    onClick={() => {
                      if (subs.length === 0) {
                        setCategoryId(parent.id); setView('main')
                      }
                    }}
                    disabled={subs.length > 0}
                  >
                    <span style={styles.pickerLabel}>
                      {parent.name}
                    </span>
                    {subs.length === 0 && effectiveCatId === parent.id && (
                      <span style={{ color: theme.colors.green, fontSize: '14px', lineHeight: 1 }} aria-hidden="true">✓</span>
                    )}
                  </button>
                  {subs.map(sub => (
                    <button
                      key={sub.id}
                      type="button" className="btn-compact"
                      style={{
                        ...styles.pickerRow,
                        paddingLeft: '52px',
                        background: effectiveCatId === sub.id ? theme.colors.greenSoft : 'transparent',
                      }}
                      onClick={() => { setCategoryId(sub.id); setView('main') }}
                    >
                      <span style={styles.pickerLabel}>
                        {sub.name}
                      </span>
                      {effectiveCatId === sub.id && <span style={{ color: theme.colors.green, fontSize: '14px', lineHeight: 1 }} aria-hidden="true">✓</span>}
                    </button>
                  ))}
                  {subs.length > 0 && (
                    <button
                      type="button" className="btn-compact"
                      style={{
                        ...styles.pickerRow,
                        paddingLeft: '52px',
                        background: effectiveCatId === parent.id ? theme.colors.greenSoft : 'transparent',
                      }}
                      onClick={() => { setCategoryId(parent.id); setView('main') }}
                    >
                      <span style={{ ...styles.pickerLabel, fontStyle: 'italic', color: theme.colors.inkMuted }}>
                        {parent.name} (general)
                      </span>
                      {effectiveCatId === parent.id && <span style={{ color: theme.colors.green, fontSize: '14px', lineHeight: 1 }} aria-hidden="true">✓</span>}
                    </button>
                  )}
                </div>
              )
            })
          )}
        </div>
      </Sheet>
    )
  }

  if (view === 'payment') {
    return (
      <Sheet onClose={onClose} title="Paid with" onBack={() => setView('main')}>
        <div style={styles.sheetBody}>
          <button
            type="button" className="btn-compact"
            style={{
              ...styles.pickerRow,
              background: !paymentMethodId ? theme.colors.greenSoft : 'transparent',
            }}
            onClick={() => { setPaymentMethodId(null); setView('main') }}
          >
            <span style={styles.pickerLabel}>Don't track</span>
            {!paymentMethodId && <span style={{ color: theme.colors.green, fontSize: '14px', lineHeight: 1 }} aria-hidden="true">✓</span>}
          </button>
          {paymentMethods.length === 0 && (
            <div style={styles.sheetEmpty}>
              No payment methods yet. Add them in Settings to track which card was used.
            </div>
          )}
          {paymentMethods.map(pm => (
            <button
              key={pm.id}
              type="button" className="btn-compact"
              style={{
                ...styles.pickerRow,
                background: paymentMethodId === pm.id ? theme.colors.greenSoft : 'transparent',
              }}
              onClick={() => { setPaymentMethodId(pm.id); setView('main') }}
            >
              <span style={styles.pickerLabel}>
                {pm.name}{pm.last_four ? ` · ${pm.last_four}` : ''}
              </span>
              {paymentMethodId === pm.id && <span style={{ color: theme.colors.green, fontSize: '14px', lineHeight: 1 }} aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      </Sheet>
    )
  }

  const taxColor = taxStatus === 'business' ? theme.colors.orange
    : taxStatus === 'charity' ? theme.colors.rose
    : theme.colors.plum

  return (
    <Sheet onClose={onClose} title="Edit transaction">
      <div style={styles.sheetBody}>
        {tx.receipt_url && (
          <div style={styles.editReceiptThumb}>
            <img src={tx.receipt_url} alt="" style={styles.editReceiptImg} />
            <span style={styles.editReceiptBadge}>
              Receipt
            </span>
          </div>
        )}

        <div style={styles.fieldList}>
          <FieldRow
            label="Merchant" value={merchantName} placeholder="e.g. Kroger"
            editing={editing === 'merchant'}
            onStartEdit={() => setEditing('merchant')}
            onCommit={() => setEditing(null)}
            onChange={setMerchantName}
          />
          <FieldRow
            label="Amount" value={amount}
            displayValue={formattedAmount} placeholder="0.00"
            editing={editing === 'amount'}
            onStartEdit={() => setEditing('amount')}
            onCommit={() => setEditing(null)}
            onChange={setAmount}
            inputType="number" inputMode="decimal" amountStyle
          />
          <button
            type="button" className="btn-compact"
            style={styles.fieldRow}
            onClick={() => setView('category')}
          >
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <div style={styles.fieldLabel}>Category</div>
              <div style={{
                ...styles.fieldValue,
                color: selectedCategory ? theme.colors.plum : theme.colors.inkQuiet,
              }}>
                {selectedCategory
                  ? selectedCategory.name
                  : 'Choose category'}
              </div>
            </div>
            <span style={{ ...styles.fieldEditIcon, fontSize: '13px', lineHeight: 1 }} aria-hidden="true">▾</span>
          </button>
          <button
            type="button"
            className="btn-compact"
            style={styles.fieldRow}
            onClick={() => {
              const el = dateInputRef.current
              if (!el) return
              // showPicker is the modern API; falls back to focus+click on older browsers
              if (typeof el.showPicker === 'function') {
                try { el.showPicker() } catch { el.focus() }
              } else {
                el.focus()
                el.click()
              }
            }}
          >
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <div style={styles.fieldLabel}>Date</div>
              <div style={styles.fieldValue}>{formattedDate}</div>
            </div>
            {/* Hidden, off-screen native date input — controlled by the button above */}
            <input
              ref={dateInputRef}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={styles.dateInputHidden}
              aria-label="Date"
              tabIndex={-1}
            />
          </button>
          <button
            type="button" className="btn-compact"
            style={{ ...styles.fieldRow, borderBottom: 'none' }}
            onClick={() => setView('payment')}
          >
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <div style={styles.fieldLabel}>Paid with</div>
              <div style={{
                ...styles.fieldValue,
                color: selectedPM ? theme.colors.plum : theme.colors.inkQuiet,
              }}>
                {selectedPM
                  ? `${selectedPM.name}${selectedPM.last_four ? ` · ${selectedPM.last_four}` : ''}`
                  : 'Optional'}
              </div>
            </div>
            <span style={{ ...styles.fieldEditIcon, fontSize: '13px', lineHeight: 1 }} aria-hidden="true">▾</span>
          </button>
        </div>

        <div style={styles.taxSegment} role="radiogroup" aria-label="Tax status">
          {[
            { id: 'personal', label: 'Personal', color: theme.colors.plum },
            { id: 'business', label: 'Business', color: theme.colors.orange },
            { id: 'charity',  label: 'Charity',  color: theme.colors.rose },
          ].map(opt => {
            const active = taxStatus === opt.id
            return (
              <button
                key={opt.id}
                type="button" role="radio" aria-checked={active}
                className="btn-compact"
                style={{
                  ...styles.segOption,
                  ...(active ? {
                    background: theme.colors.white,
                    color: opt.color,
                    boxShadow: '0 1px 3px rgba(30, 16, 64, 0.1)',
                  } : {}),
                }}
                onClick={() => setTaxStatus(opt.id)}
              >
                {opt.label}
              </button>
            )
          })}
        </div>

        {taxStatus === 'business' && (
          <div style={styles.businessMemoWrap}>
            <input
              type="text"
              value={businessMemo}
              onChange={(e) => setBusinessMemo(e.target.value)}
              placeholder="Business memo — purpose / client / project"
              style={styles.businessMemoInput}
              aria-label="Business memo"
            />
          </div>
        )}

        <textarea
          style={styles.noteInput}
          placeholder="Add a note (optional)"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        {canManualApply && onManualApply && (
          <button
            type="button" className="btn-compact"
            style={styles.editApplyBtn}
            onClick={() => onManualApply(tx)}
          >
            Apply to a bill or debt →
          </button>
        )}

        <div style={styles.editFooter}>
          <button
            type="button"
            style={{
              ...styles.editSave,
              background: taxColor,
              opacity: (!merchantName || !amount || !categoryId || saving) ? 0.5 : 1,
              cursor: (!merchantName || !amount || !categoryId || saving) ? 'not-allowed' : 'pointer',
            }}
            onClick={handleSave}
            disabled={!merchantName || !amount || !categoryId || saving}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </Sheet>
  )
}

function FieldRow({
  label, value, displayValue, placeholder,
  editing, onStartEdit, onCommit, onChange,
  inputType = 'text', inputMode, amountStyle = false, trailingIcon: Trailing,
}) {
  const inputRef = useRef(null)
  useEffect(() => {
    if (editing) setTimeout(() => inputRef.current?.focus(), 50)
  }, [editing])

  return (
    <div
      style={styles.fieldRow}
      onClick={() => !editing && onStartEdit && onStartEdit()}
      role="button" tabIndex={editing ? -1 : 0}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={styles.fieldLabel}>{label}</div>
        {editing ? (
          <input
            ref={inputRef}
            type={inputType} inputMode={inputMode}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onCommit}
            onKeyDown={(e) => { if (e.key === 'Enter') onCommit() }}
            placeholder={placeholder}
            style={amountStyle ? styles.fieldInputAmount : styles.fieldInput}
            step={inputType === 'number' ? '0.01' : undefined}
          />
        ) : (
          <div style={{
            ...(amountStyle ? styles.fieldValueAmount : styles.fieldValue),
            color: value ? theme.colors.plum : theme.colors.inkQuiet,
          }}>
            {value ? (displayValue || value) : placeholder}
          </div>
        )}
      </div>
      {Trailing && <Trailing size={14} style={styles.fieldEditIcon} aria-hidden="true" />}
    </div>
  )
}

function Sheet({ children, onClose, title, onBack }) {
  return (
    <div
      style={styles.sheetOverlay}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      role="dialog" aria-modal="true"
    >
      <div style={styles.sheet}>
        <header style={styles.sheetHeader}>
          {onBack ? (
            <button
              type="button" className="btn-compact"
              style={styles.sheetClose}
              onClick={onBack}
              aria-label="Back"
            >
              ‹
            </button>
          ) : <span style={{ width: 32 }} aria-hidden="true" />}
          <span style={styles.sheetTitle}>{title}</span>
          <button
            type="button" className="btn-compact"
            style={styles.sheetClose}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        {children}
      </div>
    </div>
  )
}

// ─── Bill match popup ────────────────────────────────────────

// Chunk 5: the "→ [bill/debt name]" indicator with a tap-to-unmatch popover.
// Self-contained (its own Cancel button), so no global outside-click plumbing.
function MatchIndicator({ name, onUnmatch }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  return (
    <span data-no-row-click="true" style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button" className="btn-compact"
        style={styles.matchTag}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o) }}
        title={`Matched to ${name}`}
      >
        → {name}
      </button>
      {open && (
        <span style={styles.unmatchPopover} role="dialog">
          <span style={styles.unmatchText}>Unmatch from {name}?</span>
          <span style={styles.unmatchBtns}>
            <button
              type="button" className="btn-compact"
              style={styles.unmatchCancel}
              onClick={(e) => { e.stopPropagation(); setOpen(false) }}
            >
              Cancel
            </button>
            <button
              type="button" className="btn-compact"
              style={styles.unmatchConfirm}
              disabled={busy}
              onClick={async (e) => {
                e.stopPropagation()
                setBusy(true)
                await onUnmatch()
                setBusy(false)
                setOpen(false)
              }}
            >
              {busy ? 'Unmatching…' : 'Unmatch'}
            </button>
          </span>
        </span>
      )}
    </span>
  )
}

function EmptyState({ hasAnyTxs, hasFilters, onClearFilters, onAddTransaction }) {
  if (hasFilters) {
    return (
      <div style={styles.empty}>
        <h2 style={styles.emptyTitle}>No matches</h2>
        <p style={styles.emptyText}>
          No transactions match the current filters. Try widening your search.
        </p>
        <button type="button" style={styles.emptyCta} onClick={onClearFilters}>
          Clear filters
        </button>
      </div>
    )
  }
  if (!hasAnyTxs) {
    return (
      <div style={styles.empty}>
        <h2 style={styles.emptyTitle}>No transactions yet</h2>
        <p style={styles.emptyText}>
          Log your first transaction. It takes 10 seconds — amount, category, done.
        </p>
        {onAddTransaction && (
          <button type="button" style={styles.emptyCta} onClick={onAddTransaction}>
            Add transaction
          </button>
        )}
      </div>
    )
  }
  return (
    <div style={styles.empty}>
      <h2 style={styles.emptyTitle}>Nothing here yet</h2>
      <p style={styles.emptyText}>
        No transactions in this view. Try a different month.
      </p>
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────

const c = theme.colors
const f = theme.fonts

const styles = {
  page: {
    maxWidth: '1100px', margin: '0 auto', width: '100%',
    padding: '24px 32px 120px',
    fontFamily: f.body, color: c.plum,
  },
  pageMobile: {
    width: '100%', paddingBottom: '120px',
    fontFamily: f.body, color: c.plum,
  },
  loading: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '10px', padding: '60px', color: c.inkMuted, fontSize: '14px',
  },

  header: {
    display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
    marginBottom: '14px', gap: '14px', flexWrap: 'wrap',
  },
  headerMobile: {
    padding: '14px 16px 10px',
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  headerLeft: { display: 'flex', flexDirection: 'column', gap: '4px' },
  pageTitle: {
    fontFamily: f.display, fontWeight: 600, fontSize: '32px',
    letterSpacing: '-0.025em', color: c.plum, margin: 0,
  },
  headerActions: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
  headerBtn: {
    background: 'transparent', color: c.plumSoft,
    border: `1px solid ${c.line}`,
    padding: '8px 14px', borderRadius: '10px',
    fontFamily: f.body, fontSize: '13px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    textDecoration: 'none',
  },
  headerBtnGreen: { background: c.green, color: '#FFFFFF', borderColor: c.green },
  headerBtnPlum: { background: c.plum, color: c.cream, borderColor: c.plum },

  filterBar: {
    display: 'flex', alignItems: 'center', gap: '10px',
    background: c.white, border: `1px solid ${c.line}`,
    borderRadius: '12px', padding: '6px 6px 6px 12px',
    marginBottom: '14px',
    boxShadow: theme.shadows.card,
  },
  filterBarMobile: {
    padding: '0 16px', marginBottom: '12px',
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  searchInput: {
    display: 'flex', alignItems: 'center', gap: '8px',
    flex: 1, minWidth: 0,
  },
  searchInputField: {
    flex: 1, minWidth: 0,
    border: 'none', outline: 'none',
    fontFamily: f.body, fontSize: '14px',
    color: c.plum, background: 'transparent', padding: '8px 0',
  },
  searchClear: {
    background: 'transparent', border: 'none', color: c.inkQuiet,
    cursor: 'pointer', display: 'grid', placeItems: 'center',
    width: '20px', height: '20px', borderRadius: '4px',
    fontSize: '16px', lineHeight: 1,
  },
  filterDivider: { width: '1px', height: '24px', background: c.line },
  chipScroll: {
    display: 'flex', alignItems: 'center', gap: '6px', overflowX: 'auto',
  },
  chipScrollMobile: {
    display: 'flex', alignItems: 'center', gap: '6px',
    overflowX: 'auto', padding: '4px 0',
    scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch',
  },
  filterChip: {
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    padding: '6px 10px',
    background: c.white, border: `1px solid ${c.line}`,
    borderRadius: '999px',
    fontFamily: f.body, fontSize: '12.5px', fontWeight: 600,
    color: c.plumSoft, cursor: 'pointer',
    flexShrink: 0, whiteSpace: 'nowrap',
    transition: 'all 120ms ease',
  },
  filterChipActive: {
    background: c.cream2, color: c.plum, borderColor: c.lineStrong,
  },
  filterChipClear: {
    width: '16px', height: '16px', borderRadius: '50%',
    background: 'rgba(30, 16, 64, 0.1)',
    color: c.plumSoft,
    display: 'grid', placeItems: 'center',
    cursor: 'pointer', marginLeft: '2px',
  },

  subHeader: {
    display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
    marginBottom: '14px', gap: '12px', flexWrap: 'wrap',
  },
  subHeaderMobile: {
    padding: '0 16px 10px',
    display: 'flex', flexDirection: 'column', gap: '10px',
  },
  totalDisplay: { display: 'flex', flexDirection: 'column', gap: '2px' },
  totalAmount: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '28px', letterSpacing: '-0.025em',
    color: c.plum, lineHeight: 1.1,
  },
  totalDetail: { fontSize: '12.5px', color: c.inkMuted },
  viewModes: {
    display: 'inline-flex', background: c.cream3,
    borderRadius: '10px', padding: '3px',
    gap: '2px', flexShrink: 0,
  },
  viewModesMobile: {
    display: 'flex', background: c.cream3,
    borderRadius: '10px', padding: '3px', gap: '2px',
  },
  viewMode: {
    padding: '7px 12px',
    background: 'transparent', border: 'none', borderRadius: '7px',
    color: c.inkMuted,
    fontFamily: f.body, fontSize: '12.5px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    flex: 1, justifyContent: 'center',
  },
  viewModeActive: {
    background: c.white, color: c.plum,
    boxShadow: '0 1px 3px rgba(30, 16, 64, 0.1)',
  },

  body: { paddingBottom: '40px' },
  bodyMobile: { padding: '0 16px 40px' },

  // Pagination footer (Chunk 10)
  pageFooter: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
    padding: '8px 16px 32px', textAlign: 'center',
  },
  pageCount: { fontSize: '12.5px', color: c.inkMuted, fontVariantNumeric: 'tabular-nums' },
  loadOlderBtn: {
    padding: '10px 20px', borderRadius: '10px',
    background: c.white, color: c.plum,
    border: `1px solid ${c.lineStrong}`,
    fontFamily: f.body, fontSize: '13.5px', fontWeight: 600, cursor: 'pointer',
  },

  group: { marginBottom: '22px' },
  groupHead: {
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
    padding: '0 6px 8px', gap: '8px', flexWrap: 'wrap',
  },
  groupTitle: { display: 'flex', alignItems: 'center', gap: '8px' },
  groupIconEmoji: { fontSize: '16px' },
  groupName: {
    fontSize: '14px', fontWeight: 600, color: c.plum,
    letterSpacing: '-0.005em',
  },
  groupCount: { fontSize: '12px', color: c.inkMuted },
  groupTotal: { display: 'flex', alignItems: 'baseline', gap: '8px' },
  groupTotalAmount: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '15px', color: c.plum, letterSpacing: '-0.015em',
  },
  groupTotalVs: {
    fontSize: '11.5px', fontWeight: 600,
  },

  rowGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },

  // Desktop transaction row (with checkbox column)
  txnRow: {
    display: 'grid',
    gridTemplateColumns: '20px 60px 30px 1fr 130px 110px 90px 28px',
    gap: '12px', alignItems: 'center',
    padding: '10px 14px',
    background: c.white, border: `1px solid ${c.line}`,
    borderRadius: '10px',
    cursor: 'pointer',
    fontFamily: f.body,
  },
  txnRowMobile: {
    display: 'flex', gap: '10px', alignItems: 'flex-start',
    padding: '12px 14px',
    background: c.white, border: `1px solid ${c.line}`,
    borderRadius: '12px',
    cursor: 'pointer',
  },
  txnRowSelected: {
    background: c.greenSoft,
    borderColor: c.green,
  },
  txnCheck: {
    width: '16px', height: '16px',
    border: `1.5px solid ${c.lineStrong}`,
    borderRadius: '4px',
    display: 'grid', placeItems: 'center',
    cursor: 'pointer', background: c.white,
    flexShrink: 0,
    marginTop: '2px',
  },
  txnCheckSelected: {
    background: c.green, borderColor: c.green,
  },
  bulkBar: {
    position: 'fixed', bottom: '24px',
    left: '50%', transform: 'translateX(-50%)',
    width: 'calc(100% - 48px)', maxWidth: '720px',
    padding: '12px 16px',
    background: c.plum, color: c.cream,
    borderRadius: '14px',
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: '12px',
    boxShadow: '0 12px 30px -8px rgba(30, 16, 64, 0.4)',
    zIndex: 50, flexWrap: 'wrap',
  },
  bulkLeft: {
    display: 'flex', alignItems: 'center', gap: '12px',
    minWidth: 0,
  },
  bulkCount: { fontSize: '13.5px', fontWeight: 600 },
  bulkClearBtn: {
    background: 'transparent', border: 'none',
    color: 'rgba(250, 247, 242, 0.7)',
    fontFamily: f.body, fontSize: '12px', fontWeight: 600,
    cursor: 'pointer', textDecoration: 'underline',
  },
  bulkActions: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  bulkBtn: {
    background: 'rgba(255, 255, 255, 0.1)',
    color: c.cream, border: 'none', borderRadius: '8px',
    padding: '7px 12px',
    fontFamily: f.body, fontSize: '12.5px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: '5px',
  },
  bulkBtnDanger: {
    background: 'rgba(190, 24, 93, 0.3)',
  },
  txnRowMobileBody: {
    flex: 1, minWidth: 0,
    display: 'flex', flexDirection: 'column', gap: '4px',
  },
  txnRowMobileTop: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: '10px',
  },
  txnRowMobileFoot: {
    display: 'flex', alignItems: 'center', gap: '6px',
    flexWrap: 'wrap',
  },

  txnDate: { fontSize: '12px', color: c.inkMuted },
  txnDay: {
    fontFamily: f.display, fontWeight: 600, fontSize: '14px',
    color: c.plum, letterSpacing: '-0.01em',
  },

  txnReceipt: {
    width: '28px', height: '28px',
    borderRadius: '6px', background: c.cream3,
    display: 'grid', placeItems: 'center',
    color: c.inkQuiet,
    border: `1px solid ${c.line}`,
    textDecoration: 'none',
    fontSize: '15px', lineHeight: 1,
  },

  txnMerchant: {
    display: 'flex', flexDirection: 'column', gap: '1px',
    minWidth: 0,
  },
  txnMerchantMobile: {
    flex: 1, minWidth: 0,
    display: 'flex', flexDirection: 'column', gap: '1px',
  },
  txnMerchantName: {
    fontSize: '14px', fontWeight: 600, color: c.plum,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  txnMerchantMeta: {
    fontSize: '11.5px', color: c.inkMuted,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },

  // Chunk 10b — inline memo line
  memoText: {
    display: 'block', marginTop: '2px', maxWidth: '100%',
    fontSize: '12.5px', color: c.inkMuted, cursor: 'text',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  memoAdd: {
    display: 'inline-block', marginTop: '2px',
    fontSize: '12px', color: c.inkQuiet, cursor: 'pointer', fontWeight: 500,
  },
  memoInput: {
    display: 'block', marginTop: '3px', width: '100%', maxWidth: '260px',
    padding: '4px 8px', borderRadius: '7px',
    border: `1px solid ${c.lineStrong}`, background: c.white,
    fontFamily: f.body, fontSize: '12.5px', color: c.plum, outline: 'none',
    boxSizing: 'border-box',
  },

  txnCategory: {
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    padding: '4px 10px',
    background: c.cream2, border: `1px solid ${c.line}`,
    borderRadius: '999px',
    fontSize: '12px', fontWeight: 600,
    color: c.plumSoft,
    width: 'fit-content', maxWidth: '100%',
    overflow: 'hidden',
  },
  txnCategoryLabel: {
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  swatch: { width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0 },

  txnPayment: {
    fontSize: '11.5px', color: c.inkMuted,
    display: 'flex', flexDirection: 'column', gap: '1px',
    minWidth: 0,
  },
  txnWho: { color: c.plumSoft, fontWeight: 600, fontSize: '11px' },
  txnPm: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  txnAmount: {
    textAlign: 'right',
    fontFamily: f.display, fontWeight: 600,
    fontSize: '15px', color: c.plum, letterSpacing: '-0.015em',
  },
  txnBusinessDot: { fontSize: '10px', fontFamily: 'inherit' },

  taxPillBusiness: {
    fontSize: '10px', fontWeight: 600,
    background: c.orangeSoft, color: c.orange,
    padding: '2px 7px', borderRadius: '999px',
    letterSpacing: '0.04em', textTransform: 'uppercase',
  },
  taxPillCharity: {
    fontSize: '10px', fontWeight: 600,
    background: c.roseSoft, color: c.rose,
    padding: '2px 7px', borderRadius: '999px',
    letterSpacing: '0.04em', textTransform: 'uppercase',
  },
  pmTagMobile: { fontSize: '10.5px', color: c.inkMuted },
  whoTagMobile: { fontSize: '10.5px', fontWeight: 600, color: c.plumSoft },
  txnReceiptLinkMobile: {
    fontSize: '10.5px', color: c.green, fontWeight: 600,
    textDecoration: 'none',
    display: 'inline-flex', alignItems: 'center', gap: '3px',
  },

  txnMenu: {
    width: '24px', height: '24px',
    background: 'transparent', border: 'none',
    color: c.inkQuiet, cursor: 'pointer',
    display: 'grid', placeItems: 'center', borderRadius: '6px',
    fontSize: '18px', lineHeight: 1,
  },
  menuPopover: {
    position: 'absolute', top: '100%', right: 0, marginTop: '4px',
    background: c.white, border: `1px solid ${c.line}`,
    borderRadius: '10px', padding: '4px',
    minWidth: '140px', zIndex: 60,
    boxShadow: theme.shadows.pop,
  },
  menuItem: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '8px 10px', width: '100%',
    border: 'none', background: 'transparent',
    fontFamily: f.body, fontSize: '13px', color: c.plum,
    fontWeight: 500, cursor: 'pointer',
    borderRadius: '6px', textAlign: 'left',
  },

  // Chunk 5 — "→ [name]" match indicator + unmatch popover
  txnMatchRow: {
    display: 'flex', alignItems: 'center', gap: '6px',
    marginTop: '2px', flexWrap: 'wrap',
  },
  matchTag: {
    display: 'inline-flex', alignItems: 'center',
    padding: '1px 6px', borderRadius: '6px',
    background: c.greenSoft, color: c.green,
    border: 'none', cursor: 'pointer',
    fontFamily: f.body, fontSize: '11.5px', fontWeight: 600,
    lineHeight: 1.5, whiteSpace: 'nowrap',
  },
  unmatchPopover: {
    position: 'absolute', top: '100%', left: 0, marginTop: '4px',
    background: c.white, border: `1px solid ${c.line}`,
    borderRadius: '10px', padding: '10px',
    minWidth: '190px', zIndex: 60,
    boxShadow: theme.shadows.pop,
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  unmatchText: { fontSize: '12.5px', color: c.plum, fontWeight: 500 },
  unmatchBtns: { display: 'flex', gap: '6px', justifyContent: 'flex-end' },
  unmatchCancel: {
    padding: '5px 10px', borderRadius: '7px',
    border: `1px solid ${c.lineStrong}`, background: 'transparent',
    fontFamily: f.body, fontSize: '12px', fontWeight: 600, color: c.plumSoft, cursor: 'pointer',
  },
  unmatchConfirm: {
    padding: '5px 10px', borderRadius: '7px',
    border: 'none', background: c.rose, color: '#FFFFFF',
    fontFamily: f.body, fontSize: '12px', fontWeight: 600, cursor: 'pointer',
  },
  editApplyBtn: {
    marginTop: '10px', padding: '11px 14px', width: '100%',
    borderRadius: '12px', border: `1px solid ${c.lineStrong}`,
    background: c.cream2, color: c.plum,
    fontFamily: f.body, fontSize: '14px', fontWeight: 600, cursor: 'pointer',
  },

  empty: {
    background: c.white, border: `1px solid ${c.line}`,
    borderRadius: '16px', padding: '40px 28px',
    textAlign: 'center', maxWidth: '480px', margin: '20px auto',
    boxShadow: theme.shadows.card,
    fontFamily: f.body,
  },
  emptyIcon: {
    width: '56px', height: '56px', borderRadius: '16px',
    background: c.cream2, color: c.plumSoft,
    display: 'grid', placeItems: 'center',
    margin: '0 auto 16px',
    border: `1px solid ${c.line}`,
  },
  emptyTitle: {
    fontFamily: f.display, fontSize: '20px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.015em', margin: '0 0 8px',
  },
  emptyText: {
    fontSize: '14px', color: c.inkMuted, lineHeight: 1.55,
    margin: '0 0 18px',
  },
  emptyCta: {
    background: c.plum, color: c.cream,
    border: 'none', padding: '11px 22px',
    borderRadius: '10px', fontFamily: f.body,
    fontSize: '13.5px', fontWeight: 600, cursor: 'pointer',
  },

  // Sheets
  sheetOverlay: {
    position: 'fixed', inset: 0, overscrollBehaviorX: 'contain',
    background: 'rgba(30, 16, 64, 0.6)',
    backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
    zIndex: 800, fontFamily: f.body,
  },
  sheet: {
    overscrollBehaviorX: 'contain',
    background: c.cream, borderRadius: '20px',
    width: '100%', maxWidth: '500px', maxHeight: '92vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 14px 50px rgba(30, 16, 64, 0.25)',
  },
  sheetHeader: {
    padding: '14px 20px 8px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    borderBottom: `1px solid ${c.line}`, flexShrink: 0,
  },
  sheetTitle: {
    fontFamily: f.display, fontSize: '18px',
    fontWeight: 600, letterSpacing: '-0.02em', color: c.plum,
    flex: 1, textAlign: 'center',
  },
  sheetClose: {
    width: '32px', height: '32px', borderRadius: '50%',
    background: 'rgba(30, 16, 64, 0.05)',
    display: 'grid', placeItems: 'center',
    cursor: 'pointer', color: c.plum, border: 'none',
    fontSize: '20px', lineHeight: 1,
  },
  sheetBody: {
    flex: 1, overflowY: 'auto',
    padding: '8px 0 calc(env(safe-area-inset-bottom, 16px) + 16px)',
  },
  sheetEmpty: {
    padding: '20px',
    fontSize: '13px', color: c.inkMuted, textAlign: 'center',
  },
  sheetSecondary: {
    flex: 1,
    padding: '11px',
    background: 'transparent', color: c.inkMuted,
    border: `1px solid ${c.lineStrong}`,
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '13.5px', fontWeight: 600,
    cursor: 'pointer',
  },
  sheetPrimary: {
    flex: 1,
    padding: '11px',
    background: c.plum, color: c.cream,
    border: 'none', borderRadius: '10px',
    fontFamily: f.body, fontSize: '13.5px', fontWeight: 600,
    cursor: 'pointer',
  },
  moreFooter: {
    display: 'flex', gap: '10px', marginTop: '14px',
    padding: '0 22px',
  },

  pickerRow: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '12px 22px', cursor: 'pointer',
    background: 'transparent', border: 'none', width: '100%',
    textAlign: 'left',
    fontFamily: f.body, fontSize: '14px', color: c.plum,
  },
  pickerLabel: {
    flex: 1, minWidth: 0,
    display: 'flex', alignItems: 'center', gap: '6px',
  },
  pickerCurrentBadge: {
    fontSize: '10px',
    background: c.greenSoft, color: c.green,
    padding: '2px 6px', borderRadius: '6px',
    fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
    marginLeft: '6px',
  },

  // Edit sheet
  editReceiptThumb: {
    margin: '12px 22px 14px', height: '120px',
    borderRadius: '12px', overflow: 'hidden', position: 'relative',
    background: c.cream2, border: `1px solid ${c.line}`,
  },
  editReceiptImg: { width: '100%', height: '100%', objectFit: 'cover' },
  editReceiptBadge: {
    position: 'absolute', bottom: '10px', left: '12px',
    background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)',
    color: '#FFFFFF',
    padding: '5px 10px', borderRadius: '999px',
    fontSize: '12px', fontWeight: 600,
    display: 'inline-flex', alignItems: 'center', gap: '6px',
  },
  fieldList: {
    margin: '0 22px',
    background: c.white, border: `1px solid ${c.line}`,
    borderRadius: '14px', overflow: 'hidden',
  },
  fieldRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: `1px solid ${c.line}`,
    cursor: 'pointer',
    background: 'transparent', border: 'none', width: '100%',
    textAlign: 'left', gap: '12px',
    fontFamily: f.body, color: c.plum,
  },
  fieldLabel: {
    fontSize: '12px', color: c.inkMuted, fontWeight: 500,
    marginBottom: '4px',
  },
  fieldValue: { fontSize: '14.5px', fontWeight: 600, color: c.plum },
  fieldValueAmount: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '18px', color: c.plum, letterSpacing: '-0.02em',
  },
  fieldInput: {
    width: '100%', border: 'none', background: 'transparent',
    outline: 'none',
    fontFamily: f.body, fontSize: '14.5px', fontWeight: 600,
    color: c.plum, padding: 0,
  },
  fieldInputAmount: {
    width: '100%', border: 'none', background: 'transparent',
    outline: 'none',
    fontFamily: f.display, fontSize: '18px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.02em', padding: 0,
  },
  fieldEditIcon: { color: c.inkQuiet, flexShrink: 0 },
  dateInput: {
    position: 'absolute', inset: 0,
    opacity: 0, border: 'none', background: 'transparent',
    cursor: 'pointer', fontSize: '14px',
  },

  taxSegment: {
    margin: '16px 22px 0', padding: '4px',
    background: c.cream3, borderRadius: '10px',
    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '2px',
  },
  segOption: {
    padding: '9px 6px', textAlign: 'center',
    fontSize: '13px', fontWeight: 600, color: c.inkMuted,
    borderRadius: '7px', cursor: 'pointer',
    border: 'none', background: 'transparent',
    fontFamily: f.body,
    transition: 'all 120ms ease',
  },

  businessMemoWrap: { margin: '10px 22px 0' },
  businessMemoInput: {
    width: '100%', padding: '11px 14px',
    background: c.white, border: `1px solid ${c.lineStrong}`,
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '13.5px',
    color: c.plum, outline: 'none', boxSizing: 'border-box',
  },
  noteInput: {
    margin: '12px 22px 0',
    width: 'calc(100% - 44px)',
    padding: '12px 14px',
    background: c.white, border: `1px solid ${c.line}`,
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '14px',
    color: c.plum, resize: 'none', outline: 'none',
    boxSizing: 'border-box',
  },
  editFooter: {
    padding: '18px 22px calc(env(safe-area-inset-bottom, 16px) + 12px)',
  },
  editSave: {
    width: '100%', height: '48px',
    background: c.plum, color: c.cream,
    border: 'none', borderRadius: '12px',
    fontFamily: f.body, fontSize: '14.5px', fontWeight: 600,
    cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '8px',
  },

  // Bill match popup
  popupOverlay: {
    position: 'fixed', inset: 0, overscrollBehaviorX: 'contain',
    background: 'rgba(30, 16, 64, 0.65)',
    backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
    fontFamily: f.body,
  },
  popupCard: {
    background: c.white,
    borderRadius: '20px',
    padding: '28px 24px 22px',
    width: '100%', maxWidth: '380px',
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(30, 16, 64, 0.25)',
  },
  popupIcon: {
    width: '56px', height: '56px',
    borderRadius: '18px',
    background: c.greenSoft, color: c.green,
    display: 'grid', placeItems: 'center',
    margin: '0 auto 14px',
  },
  popupTitle: {
    fontFamily: f.display, fontSize: '20px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.015em',
    marginBottom: '10px',
  },
  popupBody: {
    fontSize: '14px', color: c.inkMuted, lineHeight: 1.55,
    marginBottom: '20px',
  },
  popupActions: {
    display: 'flex', gap: '10px',
  },
  popupNoBtn: {
    flex: 1, padding: '11px',
    background: c.cream2, color: c.plumSoft,
    border: `1px solid ${c.line}`,
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '13.5px', fontWeight: 600,
    cursor: 'pointer',
  },
  popupYesBtn: {
    flex: 1, padding: '11px',
    background: c.green, color: '#FFFFFF',
    border: 'none', borderRadius: '10px',
    fontFamily: f.body, fontSize: '13.5px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: '6px',
  },
}
